import React, { useState, useCallback, useRef } from 'react';
import { ArrowLeftIcon } from '../icons/ArrowLeftIcon';
import { UploadIcon } from '../icons/UploadIcon';
import { DeleteIcon } from '../icons/DeleteIcon';
import { AudioRecorder } from '../shared/AudioRecorder';
import { generateScriptFromAudio, generateScriptFromConcept, generateConceptsFromProduct } from '../../services/geminiService';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { CloseIcon } from '../icons/CloseIcon';
import { EyeIcon } from '../icons/EyeIcon';
import { CREDIT_COSTS } from '../../types';

const PURPOSES = ['Product Catalog', 'Product Ads', 'Grow on Social', 'Brand Identity', 'Story & Education'];
const INDUSTRIES = ['Fashion & Beauty', 'Lifestyle', 'Food & Beverages', 'Education & Training', 'Manufacturing', 'Technology', 'Health & Fitness', 'Real Estate', 'Travel', 'Others'];
const IMAGE_COUNTS = [2, 4, 6, 8];

const ConceptOptionsModal = ({ onConfirm, onClose }) => {
    const [region, setRegion] = useState('Global');
    const [needActor, setNeedActor] = useState(false);
    const [addLogo, setAddLogo] = useState(false);
    const [numImages, setNumImages] = useState(4);
    const [purpose, setPurpose] = useState('Product Ads');
    const [industry, setIndustry] = useState('Technology');

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-[#181820] border border-gray-700 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 relative" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="close-options-modal">
                    <CloseIcon className="w-5 h-5" />
                </button>
                <h3 className="text-xl font-bold mb-5 text-center">Concept Generation Options</h3>
                
                <div className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Target Region / Country</label>
                        <input
                            type="text"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                            placeholder="e.g., USA, India, Europe, Global"
                            className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                            data-testid="region-input"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-300">Need an Actor/s?</label>
                        <div className="flex rounded-lg overflow-hidden border border-gray-700">
                            <button type="button" onClick={() => setNeedActor(true)} className={`px-4 py-1.5 text-sm font-semibold transition-colors ${needActor ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid="actor-yes">YES</button>
                            <button type="button" onClick={() => setNeedActor(false)} className={`px-4 py-1.5 text-sm font-semibold transition-colors ${!needActor ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid="actor-no">NO</button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-300">Add a Logo?</label>
                        <div className="flex rounded-lg overflow-hidden border border-gray-700">
                            <button type="button" onClick={() => setAddLogo(true)} className={`px-4 py-1.5 text-sm font-semibold transition-colors ${addLogo ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid="logo-yes">YES</button>
                            <button type="button" onClick={() => setAddLogo(false)} className={`px-4 py-1.5 text-sm font-semibold transition-colors ${!addLogo ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`} data-testid="logo-no">NO</button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Number of Images</label>
                        <div className="flex gap-2">
                            {IMAGE_COUNTS.map(n => (
                                <button key={n} type="button" onClick={() => setNumImages(n)} className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all border ${numImages === n ? 'bg-purple-600/20 border-purple-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`} data-testid={`num-images-${n}`}>{n}</button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">What is the Purpose?</label>
                        <div className="flex flex-wrap gap-2">
                            {PURPOSES.map(p => (
                                <button key={p} type="button" onClick={() => setPurpose(p)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${purpose === p ? 'bg-purple-600/20 border-purple-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`} data-testid={`purpose-${p.toLowerCase().replace(/\s+/g, '-')}`}>{p}</button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Choose the Industry</label>
                        <div className="flex flex-wrap gap-2">
                            {INDUSTRIES.map(ind => (
                                <button key={ind} type="button" onClick={() => setIndustry(ind)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${industry === ind ? 'bg-purple-600/20 border-purple-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'}`} data-testid={`industry-${ind.toLowerCase().replace(/\s+/g, '-')}`}>{ind}</button>
                            ))}
                        </div>
                    </div>
                </div>

                <button
                    onClick={() => onConfirm({ region, needActor, addLogo, numImages, purpose, industry })}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg transition-colors mt-6"
                    data-testid="confirm-generate-concepts"
                >
                    Generate Concepts
                </button>
            </div>
        </div>
    );
};

const ConceptDetailModal = ({ concept, onClose }) => {
    if (!concept) return null;
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-[#181820] border border-gray-700 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6 relative" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" data-testid="close-concept-detail-modal">
                    <CloseIcon className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-bold mb-4 pr-8" data-testid="concept-detail-title">{concept.title}</h3>
                <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-line" data-testid="concept-detail-body">{concept.fullText}</div>
            </div>
        </div>
    );
};

export const ScriptInput = ({ initialData, onSubmit, onBack, error: externalError, onDeductCredits }) => {
    const [script, setScript] = useState(initialData.script || '');
    const [mode, setMode] = useState('text');
    const [audioMode, setAudioMode] = useState('upload');
    const [audioFile, setAudioFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const audioFileInputRef = useRef(null);
    const [concept, setConcept] = useState('');
    const [productUrl, setProductUrl] = useState('');
    const [productImages, setProductImages] = useState([]);
    const [creativeBrief, setCreativeBrief] = useState(''); // User's description of what they want to create
    const productImageInputRef = useRef(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState(null);
    const [generatedConcepts, setGeneratedConcepts] = useState([]);
    const [selectedConceptId, setSelectedConceptId] = useState(null);
    const [viewingConcept, setViewingConcept] = useState(null);
    const [lastConceptOptions, setLastConceptOptions] = useState(null);
    
    const [recreateMode, setRecreateMode] = useState('url');
    const [videoUrl, setVideoUrl] = useState('');
    const [videoFile, setVideoFile] = useState(null);
    const videoFileInputRef = useRef(null);

    const [modInstructionMode, setModInstructionMode] = useState('text');
    const [modInstructionText, setModInstructionText] = useState('');
    const [modInstructionAudio, setModInstructionAudio] = useState(null);
    const [modInstructionAudioMode, setModInstructionAudioMode] = useState('upload');
    const modAudioFileInputRef = useRef(null);

    const [showStyleModal, setShowStyleModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const handleAudioFile = (file) => {
        if (file && file.type.startsWith('audio/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAudioFile({
                    name: file.name,
                    data: reader.result,
                    mimeType: file.type,
                });
            };
            reader.readAsDataURL(file);
        } else {
            setGenerationError("Invalid file type. Please upload an audio file.");
        }
    };
    
    const handleAudioFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleAudioFile(file);
        }
    };

    const handleDrop = useCallback((e, fileHandler) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            fileHandler(file);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);
    
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);
    
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const triggerGenerateScript = () => {
        if (!concept?.trim() || isGenerating) return;
        setPendingAction('concept');
        setShowStyleModal(true);
    };

    const triggerGenerateFromProductUrl = () => {
        // Allow generation if any of: productUrl, productImages, or creativeBrief is provided
        if (!productUrl?.trim() && productImages.length === 0 && !creativeBrief?.trim()) return;
        if (isGenerating) return;
        setPendingAction('productUrl');
        setShowStyleModal(true);
    };

    const handleProductImageFiles = (files) => {
        const validFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (validFiles.length === 0) {
            setGenerationError("Please upload valid image files.");
            return;
        }
        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProductImages(prev => [...prev, { name: file.name, data: reader.result, mimeType: file.type }]);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleProductImageChange = (e) => {
        if (e.target.files?.length) handleProductImageFiles(e.target.files);
    };

    const removeProductImage = (index) => {
        setProductImages(prev => prev.filter((_, i) => i !== index));
    };

    const parseConcepts = (text) => {
        if (!text || typeof text !== 'string') return [];
        
        // Strategy: Find all "Concept N" header positions, then slice text between them.
        // This ensures ACTOR DIRECTION, LOGO PLACEMENT, etc. stay inside their concept.
        const headerRegex = /(?:^|\n)\s*(?:#{1,3}\s*)?(?:\*\*\s*)?(?:Concept|Image)\s*(\d+)\s*[:\-–—.]\s*/gi;
        const headers = [];
        let match;
        while ((match = headerRegex.exec(text)) !== null) {
            headers.push({ index: match.index, num: parseInt(match[1], 10) });
        }
        
        if (headers.length === 0) {
            // Fallback: if no "Concept N" headers found, return whole text as one concept
            if (text.length > 20) {
                return [{ id: 0, title: 'Generated Concept', body: text, fullText: text }];
            }
            return [];
        }
        
        const concepts = [];
        for (let i = 0; i < headers.length && concepts.length < 4; i++) {
            const start = headers[i].index;
            const end = i + 1 < headers.length ? headers[i + 1].index : text.length;
            let block = text.substring(start, end).trim();
            
            // Trim trailing "---" separator and anything after it (strategy notes that follow)
            const separatorIdx = block.search(/\n\s*---\s*\n/);
            if (separatorIdx !== -1) {
                block = block.substring(0, separatorIdx).trim();
            }
            
            if (block.length < 20) continue;
            
            const lines = block.split('\n').filter(l => l.trim());
            let title = lines[0] || `Concept ${concepts.length + 1}`;
            // Clean markdown from title
            title = title.replace(/^#+\s*/, '').replace(/^\*\*|\*\*$/g, '').replace(/^\d+\.\s*/, '').trim();
            if (title.length > 80) title = title.substring(0, 80) + '...';
            
            const body = lines.slice(1).join('\n').trim();
            
            concepts.push({ id: concepts.length, title, body, fullText: block });
        }
        
        return concepts;
    };

    const handleSelectConcept = (id) => {
        setSelectedConceptId(prev => prev === id ? null : id);
    };

    const triggerCreateScriptFromVideo = () => {
        if ((recreateMode === 'url' && !videoUrl?.trim()) || (recreateMode === 'upload' && !videoFile)) {
            return;
        }
        setPendingAction('recreate');
        setShowStyleModal(true);
    };

    const triggerGenerateScriptFromAudio = () => {
        if (!audioFile) return;
        setPendingAction('audio');
        setShowStyleModal(true);
    };

    const handleStyleConfirm = async (options) => {
        setShowStyleModal(false);
        setIsGenerating(true);
        setGenerationError(null);

        const { region, needActor, addLogo, numImages, purpose, industry } = options;

        // Store concept options for later use in character extraction
        setLastConceptOptions({ region, needActor, addLogo, numImages, purpose, industry });

        // Extract project details from initialData
        const projectDetails = {
            name: initialData.name || '',
            duration: initialData.duration || '1-3 minutes',
            aspectRatio: initialData.aspectRatio || '16:9',
            needActor,
            addLogo,
            numImages,
            purpose,
            industry
        };

        try {
            let generatedScript = '';

            if (pendingAction === 'concept') {
                generatedScript = await generateConceptsFromProduct(concept, region, projectDetails);
            } else if (pendingAction === 'productUrl') {
                let conceptPrompt = '';
                if (productUrl?.trim()) {
                    conceptPrompt += `Analyze the product at this URL and generate creative ad concepts for it: ${productUrl}`;
                }
                if (productImages.length > 0) {
                    conceptPrompt += conceptPrompt ? '\n\nAlso analyze the attached product images for visual details, branding, and features.' : 'Analyze the attached product images and generate creative ad concepts based on the product visuals, branding, and features.';
                }
                // Add creative brief if provided
                if (creativeBrief?.trim()) {
                    if (conceptPrompt) {
                        conceptPrompt += `\n\n**User's Creative Brief / Requirements:**\n${creativeBrief.trim()}`;
                    } else {
                        // Only creative brief provided, no URL or images
                        conceptPrompt = `Generate creative ad concepts based on the following brief:\n\n${creativeBrief.trim()}`;
                    }
                }
                generatedScript = await generateConceptsFromProduct(
                    conceptPrompt, region,
                    { ...projectDetails, productImages: productImages.map(img => img.data), creativeBrief: creativeBrief?.trim() || '' }
                );
            } else if (pendingAction === 'audio' && audioFile) {
                generatedScript = await generateScriptFromAudio(audioFile.data, 'none', region, 'English');
            } else if (pendingAction === 'recreate') {
                let videoInputData;

                if (recreateMode === 'upload' && videoFile) {
                    videoInputData = videoFile;
                } else if (recreateMode === 'url' && videoUrl) {
                    try {
                        setGenerationError("Fetching video from URL... This may take a moment.");
                        const response = await fetch(videoUrl);
                        if (!response.ok) throw new Error(`Failed to fetch video from URL: ${response.statusText}`);
                        const blob = await response.blob();
                        const mimeType = blob.type;
                        if (!mimeType.startsWith('video/')) throw new Error('The provided URL does not point to a valid video file.');
                        const dataUrl = await new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                        });
                        videoInputData = { data: dataUrl, mimeType };
                    } catch (fetchError) {
                        console.error(fetchError);
                        throw new Error("Could not fetch video from URL. Please try uploading the video directly.");
                    }
                }
                
                setGenerationError(null); 

                const instructions = {};
                if (modInstructionText?.trim()) instructions.text = modInstructionText;
                if (modInstructionAudio) instructions.audio = modInstructionAudio;

                // Credits are deducted by backend - removed upfront deduction
                
                generatedScript = await generateScriptFromVideo(
                    videoInputData,
                    Object.keys(instructions).length > 0 ? instructions : undefined,
                    style,
                    region,
                    language
                );
            }

            if (generatedScript) {
                // For concept/productUrl actions, parse into selectable cards
                if (pendingAction === 'concept' || pendingAction === 'productUrl') {
                    const parsed = parseConcepts(generatedScript);
                    setGeneratedConcepts(parsed);
                    setSelectedConceptId(parsed.length > 0 ? parsed[0].id : null);
                } else {
                    setScript(generatedScript);
                    setMode('text');
                }
            }

        } catch (err) {
            const message = err instanceof Error ? err.message : "An unknown error occurred during script generation.";
            setGenerationError(message);
        } finally {
            setIsGenerating(false);
            setPendingAction(null);
        }
    };

    const handleVideoFile = (file) => {
        if (file && file.type.startsWith('video/')) {
            const MAX_SIZE_MB = 50;
            if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                setGenerationError(`Video file is too large. Please select a file smaller than ${MAX_SIZE_MB}MB.`);
                return;
            }
            setGenerationError(null);
            const reader = new FileReader();
            reader.onloadend = () => {
                setVideoFile({
                    name: file.name,
                    data: reader.result,
                    mimeType: file.type,
                });
                setVideoUrl('');
            };
            reader.readAsDataURL(file);
        } else {
            setGenerationError("Invalid file type. Please upload a video file.");
        }
    };

    const handleVideoFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleVideoFile(file);
        }
    };

    const handleModAudioFile = (file) => {
        if (file && file.type.startsWith('audio/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setModInstructionAudio({
                    name: file.name,
                    data: reader.result,
                    mimeType: file.type,
                });
            };
            reader.readAsDataURL(file);
        } else {
            setGenerationError("Invalid file type. Please upload an audio file for the instructions.");
        }
    };

    const handleModAudioFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleModAudioFile(file);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (mode === 'text' && script?.trim()) {
            onSubmit({ script });
        } else if (mode === 'audio' && audioFile) {
            onSubmit({ audio: audioFile });
        } else if (mode === 'concept' && generatedConcepts.length > 0 && selectedConceptId !== null) {
            const selected = generatedConcepts.find(c => c.id === selectedConceptId);
            if (selected) onSubmit({ script: selected.fullText, conceptOptions: lastConceptOptions });
        } else if (mode === 'concept' && generationError && concept?.trim()) {
            onSubmit({ script: concept, conceptOptions: lastConceptOptions });
        }
    };

    const isSubmitDisabled = (mode === 'text' && !script?.trim()) || 
                             (mode === 'audio' && !audioFile) || 
                             (mode === 'concept' && generatedConcepts.length === 0 && !generationError) ||
                             (mode === 'concept' && generatedConcepts.length > 0 && selectedConceptId === null);

    return (
        <>
            {showStyleModal && (
                <ConceptOptionsModal 
                    onConfirm={handleStyleConfirm} 
                    onClose={() => { setShowStyleModal(false); setPendingAction(null); }} 
                />
            )}
            {viewingConcept && (
                <ConceptDetailModal
                    concept={viewingConcept}
                    onClose={() => setViewingConcept(null)}
                />
            )}
            <div className="w-full h-full overflow-y-auto p-8">
                <div className="w-full max-w-2xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-2">What's Your Story?</h2>
                    <p className="text-gray-400 text-center mb-6">Provide your concept as a script, a simple concept, or a voice narration.</p>

                    <div className="flex justify-center mb-6">
                        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-1 flex space-x-1 flex-wrap justify-center">
                            <button
                                type="button"
                                onClick={() => setMode('text')}
                                className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${mode === 'text' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                            >
                                Write Concept
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('concept')}
                                className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${mode === 'concept' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                            >
                                Create Concepts using Product URL/Images
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('audio')}
                                className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${mode === 'audio' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
                            >
                                Upload Narration
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {mode === 'text' && (
                            <textarea
                                value={script}
                                onChange={(e) => setScript(e.target.value)}
                                placeholder="e.g., A detective in a rain-soaked city chases a phantom that only appears in reflections..."
                                className="w-full h-60 p-3 bg-gray-800/50 border border-gray-700 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                            />
                        )}
                        {mode === 'concept' && (
                             <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Product URL</label>
                                    <input
                                        type="url"
                                        value={productUrl}
                                        onChange={(e) => setProductUrl(e.target.value)}
                                        placeholder="https://example.com/product-page"
                                        className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                                        disabled={isGenerating}
                                        data-testid="product-url-input"
                                    />
                                    <p className="text-xs text-gray-500 mt-1.5">Paste a product page URL to study product details.</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Product Images <span className="text-gray-500 font-normal">(optional)</span></label>
                                    <input type="file" accept="image/*" multiple ref={productImageInputRef} onChange={handleProductImageChange} className="hidden" />
                                    <div
                                        onClick={() => productImageInputRef.current?.click()}
                                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); if (e.dataTransfer.files?.length) handleProductImageFiles(e.dataTransfer.files); }}
                                        onDragOver={handleDragOver}
                                        onDragEnter={handleDragEnter}
                                        onDragLeave={handleDragLeave}
                                        className={`w-full p-4 border border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-gray-500 bg-gray-800/30'}`}
                                        data-testid="product-image-dropzone"
                                    >
                                        <UploadIcon className="w-8 h-8 text-gray-500 mb-1" />
                                        <p className="text-gray-400 text-sm">Drop product images here or click to browse</p>
                                    </div>
                                    {productImages.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {productImages.map((img, i) => (
                                                <div key={i} className="relative group w-16 h-16 rounded-md overflow-hidden border border-gray-700">
                                                    <img src={img.data} alt={img.name} className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeProductImage(i)}
                                                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                                                        data-testid={`remove-product-image-${i}`}
                                                    >
                                                        <DeleteIcon className="w-4 h-4 text-red-400" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Creative Brief / Description */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Describe what you want to create <span className="text-gray-500 font-normal">(optional)</span>
                                    </label>
                                    <textarea
                                        value={creativeBrief}
                                        onChange={(e) => setCreativeBrief(e.target.value)}
                                        placeholder="E.g., Create a fun, energetic ad showcasing the product's key features. Target audience: young professionals aged 25-35. Tone: modern and playful..."
                                        rows={3}
                                        className="w-full p-3 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all resize-none"
                                        disabled={isGenerating}
                                        data-testid="creative-brief-input"
                                    />
                                    <p className="text-xs text-gray-500 mt-1.5">Add any specific requirements, target audience, tone, or creative direction.</p>
                                </div>
                                
                                {generationError && <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm">{generationError}</div>}
                                <button
                                    type="button"
                                    onClick={triggerGenerateFromProductUrl}
                                    disabled={(!productUrl?.trim() && productImages.length === 0 && !creativeBrief?.trim()) || isGenerating}
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors text-lg flex items-center justify-center"
                                    data-testid="generate-from-url-btn"
                                >
                                    {isGenerating ? <LoadingSpinner className="w-5 h-5 mr-3" /> : <MagicWandIcon className="w-5 h-5 mr-3" />}
                                    {isGenerating ? 'Generating Concepts...' : generatedConcepts.length > 0 ? 'Regenerate Concepts' : 'Generate Concepts'}
                                </button>

                                {generatedConcepts.length > 0 && (
                                    <div className="space-y-3 mt-2">
                                        <h3 className="text-sm font-semibold text-gray-300" data-testid="concept-selection-header">
                                            Select a Concept ({generatedConcepts.length} generated)
                                        </h3>
                                        {generatedConcepts.map((c) => {
                                            const isSelected = selectedConceptId === c.id;
                                            return (
                                                <div
                                                    key={c.id}
                                                    onClick={() => handleSelectConcept(c.id)}
                                                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                                                        isSelected 
                                                            ? 'border-purple-500 bg-purple-600/10 ring-1 ring-purple-500/50' 
                                                            : 'border-gray-700 bg-gray-800/40 hover:border-gray-500'
                                                    }`}
                                                    data-testid={`concept-card-${c.id}`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-purple-500 bg-purple-600' : 'border-gray-600 bg-transparent'}`}>
                                                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-semibold text-white text-sm mb-1">{c.title}</h4>
                                                            <p className="text-gray-400 text-xs leading-relaxed whitespace-pre-line line-clamp-3">{c.body}</p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => { e.stopPropagation(); setViewingConcept(c); }}
                                                            className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                                                            title="View full concept"
                                                            data-testid={`view-concept-${c.id}`}
                                                        >
                                                            <EyeIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        {mode === 'audio' && (
                            <div>
                                 {audioFile ? (
                                    <div className="space-y-4">
                                        <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-lg flex items-center justify-between">
                                            <p className="text-gray-300 truncate">{audioFile.name}</p>
                                            <button
                                                type="button"
                                                onClick={() => setAudioFile(null)}
                                                className="p-1 rounded-full text-red-400 hover:bg-red-500/20"
                                            >
                                                <DeleteIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                        {generationError && <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm">{generationError}</div>}
                                        <button
                                            type="button"
                                            onClick={triggerGenerateScriptFromAudio}
                                            disabled={isGenerating}
                                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors text-lg flex items-center justify-center"
                                        >
                                            {isGenerating ? <LoadingSpinner className="w-5 h-5 mr-3" /> : <MagicWandIcon className="w-5 h-5 mr-3" />}
                                            {isGenerating ? 'Generating Script...' : 'Generate Script from Audio'}
                                        </button>
                                    </div>
                                 ) : (
                                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg">
                                        <div className="flex border-b border-gray-700">
                                            <button
                                                type="button"
                                                onClick={() => setAudioMode('upload')}
                                                className={`flex-1 py-3 text-center font-semibold transition-colors ${audioMode === 'upload' ? 'bg-purple-600/20 text-purple-300' : 'text-gray-400 hover:bg-gray-700/50'}`}
                                            >
                                                Upload File
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setAudioMode('record')}
                                                className={`flex-1 py-3 text-center font-semibold transition-colors ${audioMode === 'record' ? 'bg-purple-600/20 text-purple-300' : 'text-gray-400 hover:bg-gray-700/50'}`}
                                            >
                                                Record Audio
                                            </button>
                                        </div>
                                        <div>
                                            {audioMode === 'upload' ? (
                                                <>
                                                    <input type="file" accept="audio/*" ref={audioFileInputRef} onChange={handleAudioFileChange} className="hidden" />
                                                    <div
                                                        onDrop={(e) => handleDrop(e, handleAudioFile)}
                                                        onDragOver={handleDragOver}
                                                        onDragEnter={handleDragEnter}
                                                        onDragLeave={handleDragLeave}
                                                        onClick={() => audioFileInputRef.current?.click()}
                                                        className={`w-full h-60 p-3 rounded-b-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${isDragging ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-gray-500'}`}
                                                    >
                                                        <UploadIcon className="w-12 h-12 text-gray-500 mb-2" />
                                                        <p className="text-gray-400">Drag & drop your audio file here</p>
                                                        <p className="text-gray-500 text-sm">or click to browse</p>
                                                    </div>
                                                </>
                                            ) : (
                                                <AudioRecorder onRecordingComplete={setAudioFile} />
                                            )}
                                        </div>
                                    </div>
                                 )}
                            </div>
                        )}
                        
                        {(generationError || externalError) && (
                            <div className="bg-red-500/20 border border-red-500 text-red-300 p-4 rounded-lg text-sm">
                                {externalError || generationError}
                            </div>
                        )}
                        
                        <div className="flex items-center space-x-4">
                             <button
                                type="button"
                                onClick={onBack}
                                className="w-1/3 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center transition-colors"
                            >
                                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                                Back
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitDisabled}
                                className="w-2/3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors text-lg"
                            >
                                {mode === 'concept' && selectedConceptId !== null 
                                    ? 'Continue with Selected Concept'
                                    : 'Continue to Characters'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};
